import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonProps, CardHeader, CircularProgress } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import useModal from '@yaydoo/react-components/lib/hooks/useModal';

import logoGreen from '@assets/images/logo_green.svg';
import masterCardIcon from '@assets/images/mastercard.svg';
import safeKeyIcon from '@assets/images/safekey.svg';
import visaIcon from '@assets/images/visa.svg';
import Button from '@components/common/Button';

import useStyles from './styles';
import useStore from '@hooks/useStore';

export interface IContinueButton extends ButtonProps {}

export interface IRedirect3dSecure {
  redirectUrl: string;
  amount: string | number;
  onClose: () => void;
  delay?: number;
  callback?: () => void;
  showCountdown?: boolean;
  waitingMessage?: string;
  continueButton?: IContinueButton;
}
const Redirect3dSecure: FC<IRedirect3dSecure> = ({
  redirectUrl,
  amount,
  onClose,
  delay = 5000,
  callback,
  showCountdown = false,
  waitingMessage,
  continueButton,
}) => {
  const {
    links: {
      coBrandingConfig: { primaryColor, secondaryColor },
    },
  } = useStore();
  const css = useStyles({ primaryColor, secondaryColor });
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [countdown, setCountDown] = useState(delay / 1000);
  const continueButtonProps = { disabled: true, ...continueButton };

  useEffect(() => {
    if (showCountdown) {
      const timerCountdown = countdown > 0 && setInterval(() => setCountDown(countdown - 1), 1000);
      return () => clearInterval(timerCountdown as NodeJS.Timeout);
    }
  }, [countdown, showCountdown]);

  let timer: NodeJS.Timeout;
  useLayoutEffect(() => {
    timer = setTimeout(() => (callback ? callback() : (window.location.href = redirectUrl)), delay);
  }, []);

  const closeHandler = () => {
    if (timer) {
      clearTimeout(timer);
    }
    closeModal();
    onClose && onClose();
  };

  return (
    <Card className={css.modal}>
      <CardHeader
        className={css.header}
        avatar={<img src={logoGreen} className="oyster-logo" alt="oyster" />}
        action={
          <IconButton
            data-testid="3ds-redirect-close"
            className={css.close}
            size="small"
            onClick={closeHandler}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
      <CardContent className={css.content}>
        <section className={css.section}>
          <h3 data-testid="3ds-redirect-amount" className={css.price}>
            {amount}
          </h3>
          <Typography gutterBottom>
            <span className={css.subtitle}>
              {t('checkoutScreen.payment.methods.card.secure3d.amount')}
            </span>
          </Typography>
          <Typography variant="caption" className={css.info}>
            {t('checkoutScreen.payment.methods.card.secure3d.info')}
          </Typography>
          <Typography className={css.verified}>
            {t('checkoutScreen.payment.methods.card.secure3d.verifiedBy')}
          </Typography>
          <div className={css.icons}>
            <img data-testid="3ds-redirect-visa" className="brand visa" src={visaIcon} alt="visa" />
            <img
              data-testid="3ds-redirect-mastercard"
              className="brand mastercard"
              src={masterCardIcon}
              alt="master-card"
            />
            <img
              data-testid="3ds-redirect-amex"
              className="brand amex"
              src={safeKeyIcon}
              alt="amex"
            />
          </div>
        </section>
        <section className={`${css.section} loading`}>
          <div className={css.redirecting}>
            {!continueButtonProps.disabled ? (
              <Button {...continueButtonProps}>{t('common.labels.continue')}</Button>
            ) : (
              <>
                {showCountdown && <Typography variant="h3">{countdown}</Typography>}
                <CircularProgress
                  className={css.progress}
                  data-testid="3ds-redirect-progress"
                  size={25}
                />
                <Typography variant="caption">
                  {waitingMessage
                    ? waitingMessage
                    : t('checkoutScreen.payment.methods.card.secure3d.redirecting')}
                </Typography>
              </>
            )}
          </div>
        </section>
      </CardContent>
    </Card>
  );
};

export default Redirect3dSecure;
